import {
  IPassageway,
  IPassagewaysParam,
  IPointGeometry,
  IResult,
} from "@/types/map";
import axios from "axios";
import { loadModules } from "esri-loader";
import WallLayer from "../../Animation/WallLayer";
import { OverlayArcgis3D } from "../../Overlays/arcgis/OverlayArcgis3D";
import { checkPoints } from "./CheckPoints";

export default class PassagewayWall {
  private static intances: Map<string, PassagewayWall>;
  private maxHeight: number = 3190;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!PassagewayWall.intances) {
      PassagewayWall.intances = new Map();
    }
    let instance = PassagewayWall.intances.get(id);
    if (!instance) {
      instance = new PassagewayWall(view);
      PassagewayWall.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.SceneView;

  private roadLayer!: __esri.FeatureLayer;
  private webMercatorUtils!: __esri.webMercatorUtils;
  private geometryEngine!: __esri.geometryEngine;

  private constructor(view: __esri.SceneView) {
    this.view = view;
  }

  private async clear() {
    const overlay = OverlayArcgis3D.getInstance(this.view);
    overlay.deleteOverlays({ types: ["passagewaywall"] });

    const wallLayer = WallLayer.getInstance(this.view);
    wallLayer.deleteAllWalls();
  }

  public deletePassageWall() {
    this.clear();
  }

  public async initPassageWall(params: { roadUrl: string }): Promise<IResult> {
    type MapModules = [
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/layers/support/Field"),
      typeof import("esri/Graphic"),
      typeof import("esri/geometry/support/webMercatorUtils"),
      typeof import("esri/geometry/geometryEngine")
    ];
    const [
      FeatureLayer,
      Field,
      Graphic,
      webMercatorUtils,
      geometryEngine,
    ] = await (loadModules([
      "esri/layers/FeatureLayer",
      "esri/layers/support/Field",
      "esri/Graphic",
      "esri/geometry/support/webMercatorUtils",
      "esri/geometry/geometryEngine",
    ]) as Promise<MapModules>);

    this.webMercatorUtils = webMercatorUtils;
    this.geometryEngine = geometryEngine;

    if (params.roadUrl.endsWith("json")) {
      try {
        const responseData = await axios.get(params.roadUrl);

        if (responseData.status === 200) {
          const graphics = responseData.data.features.map((feature: any) => {
            return Graphic.fromJSON(feature);
          });
          this.roadLayer = new FeatureLayer({
            objectIdField: "FID",
            source: graphics,
            fields: responseData.data.fields.map((field: any) => {
              return Field.fromJSON(field);
            }),
            renderer: {
              type: "simple",
              symbol: {
                type: "simple-line",
                color: "red",
                width: 6,
              },
            } as any,
          });

          return { status: 0, message: "" };
        } else {
          return { status: -1, message: "路段url错误" };
        }
      } catch (err) {
        return { status: -1, message: "路段url错误" };
      }
    } else {
      this.roadLayer = new FeatureLayer({ url: params.roadUrl });
      return { status: 0, message: "" };
    }
  }

  public async addPassageWall(params: IPassagewaysParam) {
    this.clear();

    let overlays: any[] = [];
    let routePoints: IPointGeometry[] = [];
    let pathMap: Map<string, any> = new Map<string, any>();
    let paths = params.ways.map((way: IPassageway, index: number) => {
      return way.crossId;
    });
    let show = params.show !== false;
    //根据路口号查找坐标
    checkPoints.forEach((checkPoint) => {
      if (checkPoint.type === "cross") {
        let num = paths.indexOf(checkPoint.id);
        if (num > -1) {
          pathMap.set(checkPoint.id, {
            x: checkPoint.position[0],
            y: checkPoint.position[1],
          });
        }
      }
    });
    params.ways.forEach((way: IPassageway, index: number) => {
      let url = ";";
      let size = [30, 30];
      if (way.type == "非协调路口") {
        url = "assets/image/road_fx.png";
        size = [32, 44];
      } else if (way.type == "协调路口") {
        url = "assets/image/road_xt.png";
        size = [32, 44];
      } else if (way.type == "关键路口") {
        url = "assets/image/road_key.png";
        size = [32, 44];
      } else {
        url = "assets/image/road_nomal.png";
        size = [32, 44];
      }
      overlays.push({
        geometry: pathMap.get(way.crossId),
        id: way.crossId,
        field: { id: way.crossId, type: way.type },
        symbol: {
          url,
          size,
          anchor: "bottom",
        },
      });
    });
    const overlay = OverlayArcgis3D.getInstance(this.view);
    overlay.addOverlays({ type: "passagewaywall", overlays: overlays });
    let geometrys: any[] = [];
    for (let i = 0; i < paths.length - 1; i++) {
      const id1 = paths[i];
      const id2 = paths[i + 1];
      let geometry: any = await this.getRoadGeometry(id1, id2);
      if (geometry) {
        geometrys.push(geometry);
        const path = geometry.paths[0];
        path.forEach((point: any) => {
          routePoints.push({
            x: point[0],
            y: point[1],
          });
        });
      }
    }
    if (geometrys.length === 0) {
      return;
    }
    let unionGeometry = this.geometryEngine.union(geometrys);
    let geoExtent = unionGeometry.extent;
    if (geoExtent.spatialReference.isWGS84) {
      geoExtent = this.webMercatorUtils.geographicToWebMercator(
        geoExtent
      ) as __esri.Extent;
    }
    if (show) {
      if (geoExtent.height >= this.maxHeight) {
        this.view.goTo({ target: geometrys, scale: 15000, tilt: 65 });
      } else {
        this.view.goTo({ target: geometrys });
      }
    }
    const wallLayer = WallLayer.getInstance(this.view);
    await wallLayer.addWalls({
      overlays: [
        {
          geometry: {
            path: routePoints,
          },
        },
      ],
    });
  }

  private async getRoadGeometry(id1: string, id2: string) {
    // type MapModules = [typeof import("esri/layers/FeatureLayer")];
    // const [FeatureLayer] = await (loadModules([
    //   "esri/layers/FeatureLayer",
    // ]) as Promise<MapModules>);
    // const roadLayer = new FeatureLayer({
    //   url: `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/Unicron/ShangHai_Road/MapServer/8`,
    // });
    const query = this.roadLayer.createQuery();
    query.where = `(P_FNode = '${id1}' AND P_TNode='${id2}') or (N_FNode = '${id1}' AND N_TNode='${id2}')`;
    query.outFields = ["*"];
    query.returnGeometry = true;
    const features = (await this.roadLayer.queryFeatures(query)).features;

    if (features.length > 0) {
      if (features[0].attributes.P_FNode == `${id1}`) {
        return features[0].geometry as __esri.Polyline;
      } else {
        (features[0].geometry as __esri.Polyline).paths[0].reverse();
        return features[0].geometry as __esri.Polyline;
      }
    } else {
      return undefined;
    }
  }
}
